import React from 'react';

import { FormProvider } from 'react-hook-form';
import { observer } from 'mobx-react';
import { enqueueSnackbar } from 'notistack';

import { ButtonsRow } from '../../components/ButtonsRow/ButtonsRow';
import { UserProfileFromValue } from './UserProfileStep.settings';
import { getUserProfileFromValue } from './utils/getUserProfileFromValue';
import { customerService } from '../../../../services/customerService';
import { processAssignCustomersToGroup } from '../../../../../customer-group/utils/processAssignCustomersToGroup';
import { useInbodyData } from './hooks/useInbodyData';
import { useCustomerScan } from '../../../../../inbody';
import { processCreateInbodyScan } from '../../../../../inbody/utils/processCreateInbodyScan/processCreateInbodyScan';
import { AddInbodyScan } from './AddInbodyScan/AddInbodyScan';
import { customerSetupStore } from '../../../../stores/customerSetupStore';
import { getFilteredTruthyFieldsObj } from '../../../../../shared/utils/sortingAndGroupingUtil';
import { CustomerProfileForm } from '../../../../components/CustomerProfileForm/CustomerProfileForm';
import { useCustomerProfileForm } from '../../../../hooks/useCustomerProfileForm';

type UserProfileStepProps = {
  customerId: number;
};

const UserProfileStep = observer(({ customerId }: UserProfileStepProps) => {
  const { stepperMethods, customerInfo, setCustomerInfo, setCustomerBodyMetrics } =
    customerSetupStore;
  const entityId = customerSetupStore.getEntityId();

  const { defaultValues, formMethods } = useCustomerProfileForm<UserProfileFromValue>(
    () => getUserProfileFromValue(customerInfo),
  );

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = formMethods;

  const { entityInbodyData, supportedInbodyTypeOptions } = useInbodyData(entityId);
  const { scanData, isScanUnavailable, getScanData } = useCustomerScan();

  const handleFormSubmit = handleSubmit(
    async ({
      user,
      customer,
      inbodyInfo,
      additionallyInfo,
      customerGroup,
      scanData: manualScanData,
    }) => {
      if (!scanData && !manualScanData) {
        enqueueSnackbar(
          "Please select 'Load scan from InBody' or 'Fill in data manually' to proceed further.",
          { variant: 'error', autoHideDuration: 20000 },
        );
      } else {
        await processAssignCustomersToGroup(customerId, customerGroup || null);
        const updatedInfo = await customerService.setupCustomerProfile(customerId, {
          user,
          customer,
          inbodyInfo,
          additionallyInfo: {
            ...getFilteredTruthyFieldsObj(additionallyInfo),
            sportType: additionallyInfo.sportType,
          },
        });

        // customer inbody type can be changed by previous api call
        await processCreateInbodyScan(customerId, inbodyInfo.inbodyType, manualScanData);

        setCustomerInfo(updatedInfo);

        const customerScanDate = scanData || manualScanData;

        setCustomerBodyMetrics({
          weight: customerScanDate!.weight,
          percentBodyFat: customerScanDate!.percentBodyFat,
        });
        stepperMethods.goToNextStep();
      }
    },
  );

  return (
    <div>
      <FormProvider {...formMethods}>
        <CustomerProfileForm
          mode="CREATE"
          control={control}
          entityId={entityId}
          defaultValues={defaultValues}
          supportedInbodyTypeOptions={supportedInbodyTypeOptions}
          InbodyInfoFormChildren={
            <AddInbodyScan
              entityInbodyData={entityInbodyData}
              defaultManualScanData={defaultValues?.scanData}
              scanData={scanData}
              isScanUnavailable={isScanUnavailable}
              getScanData={getScanData}
            />
          }
        />
        <ButtonsRow onNext={handleFormSubmit} yesDisabled={isSubmitting} />
      </FormProvider>
    </div>
  );
});

export { UserProfileStep };
